import { useEffect, useState } from 'react';

import { Box } from 'components/Box/Box';
import { EventDetailsList } from 'components/DetailsList/Event/EventDetailsList';
import { PageToolbar } from 'components/PageToolbar/PageToolbar';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { configValue } from 'config/appConfigUtils';
import { useUser } from 'contexts/UserEnvironmentContext';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import { useI18n } from 'i18n/hooks/useI18n';
import { ListingViewAdminTools } from 'modules/listing/components/ListingView/AdminTools/ListingViewAdminTools';
import { ListingViewBody } from 'modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewFooter } from 'modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from 'modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from 'modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from 'modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from 'modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSidebar } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSidebarContent } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarContent';
import { ListingViewSidebarEventAttendees } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarEventAttendees';
import { ListingViewSidebarEventHost } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarEventHost';
import { ListingViewSidebarImage } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarImage';
import { ListingViewSidebarLinks } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarLinks';
import { ListingViewSidebarTitle } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarTitle';
import { ListingViewSignupCta } from 'modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from 'modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewApplySection } from 'modules/listing/components/ListingView/sections/ListingViewApplySection';
import { ListingViewCommentSection } from 'modules/listing/components/ListingView/sections/ListingViewCommentSection';
import { ListingViewLocationSection } from 'modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { useListingApiFetchRsvpData } from 'modules/listing/events/api/listingApiFetchRsvpData';
import type { ApiEvent } from 'modules/listing/events/api/types/ApiEvent';
import { ViewEventActionButtons } from 'modules/listing/events/components/ActionButtons/ViewEventActionButtons';
import { RsvpAnswerContext } from 'modules/listing/events/hooks/useRsvpAnswer';
import { EventShowAllRsvpsModalLoadable } from 'modules/listing/events/modals/EventShowAllRsvpsModalLoadable';
import { useSaveListingAndOrg } from 'modules/listing/hooks/useSaveListingAndOrg';
import { SearchContextHeader } from 'modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from 'modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackListing } from 'utils/analytics/trackListing';
import { isUserOrgOwner } from 'utils/listings/getOrgOwner';
import { isExternalURL } from 'utils/url/isExternalURL';
import { showModal } from 'zustand-stores/modalStore';

type Props = {
  event: ApiEvent;
  initiallySaved: boolean;
  orgInitiallySaved: boolean;
};

export function ViewEventPage({
  event,
  initiallySaved,
  orgInitiallySaved,
}: Props) {
  const { i18n } = useI18n();

  const {
    areasOfFocus,
    address,
    applyText,
    applyTextTruncated,
    applyUrl,
    applyEmail,
    id,
    startDate,
    directions,
    endDate,
    startsLocal,
    endsLocal,
    firstPublished,
    feeAmount,
    feeRequired,
    hasRsvp,
    name,
    timezone,
    welcomeGroups,
    welcomeFamilies,
    detailsWheelchairAccessible,
    type,
    image,
    description,
    descriptionTruncated,
    remoteOptions,
    url,
    isIdealistDay,
    creator,
    org,
  } = event;

  const { searchContext } = useSearchContextHeader({ currentObjectId: id });

  const { user } = useUser();
  const [answer, setAnswer] = useState<string | null>(null);
  const [showRsvpsModal, setShowRsvpsModal] = useState(false);
  const isOrgOwner = isUserOrgOwner(user, org?.id);
  const openwebEnabled = configValue('openwebSpotId');
  const showComments = openwebEnabled && event.hasComments;

  const [rsvpDataResult, { reload, loading: rsvpDataLoading }] =
    useListingApiFetchRsvpData(event.id);

  useSetDocumentTitle(name);

  useEffect(() => {
    // Refresh RSVP data when current user's own RSVP answer changes
    if (answer) void reload();
  }, [answer, reload]);

  useEffect(() => {
    trackListing({
      status: 'published',
      hasAts: false,
      listingType: type,
      listingId: id,
      listingName: name,
      orgId: org?.id,
      orgName: org?.name,
      orgType: org?.orgType,
    });
  }, [id, name, org?.id, org?.name, org?.orgType, type]);

  const orgLogo = org?.logo ?? null;
  const orgName = org?.name ?? null;
  const orgUrl = org?.url[CURRENT_LOCALE()] ?? null;

  const {
    save,
    saved,
    unsave,
    orgSaved,
    saveOrg,
    unsaveOrg,
    shouldShowSaveOrgTooltip,
    dismissSaveOrgTooltip,
  } = useSaveListingAndOrg({
    listing: event,
    initiallySaved,
    orgInitiallySaved,
    searchSavedListingsStore: null,
  });

  return (
    <RsvpAnswerContext.Provider
      value={{
        answer,
        setAnswer,
      }}
    >
      {isOrgOwner && org && (
        <PageToolbar
          centered
          message={i18n('Your organization posted this event')}
          tools={
            <ListingViewAdminTools
              listingId={id}
              listingType="EVENT"
              orgId={org.id}
              canPost={Boolean(
                user?.orgs.find((o) => o.id === org.id)?.canPostEvents,
              )}
              hasAts={false}
            />
          }
        />
      )}

      <ListingViewBody>
        <ListingViewSidebar maxWidth={288}>
          <ListingViewSidebarImage
            orgLogo={orgLogo}
            orgName={orgName}
            orgURLForCurrentLocale={orgUrl}
          />
          <ListingViewSidebarTitle
            address={address}
            listingName={name}
            listingType="EVENT"
            orgName={orgName}
            orgUrl={orgUrl}
            remoteOptions={remoteOptions}
          />
          <ListingViewSidebarContent
            firstPublished={firstPublished}
            listingType={type}
            hasAts={false}
            isExpired={false}
            isPreview={false}
            applied={false}
          />

          {hasRsvp && (
            <ListingViewSidebarEventAttendees
              loading={rsvpDataLoading}
              rsvps={rsvpDataResult?.rsvps}
              count={rsvpDataResult?.count}
              onClick={() => setShowRsvpsModal(true)}
            />
          )}

          {hasRsvp && creator && (
            <ListingViewSidebarEventHost creator={creator} />
          )}

          <ListingViewSidebarLinks
            searchContext={searchContext}
            orgUrl={orgUrl}
          />
        </ListingViewSidebar>

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewHeader
            address={address}
            hasRsvp={hasRsvp}
            isPostedAnonymously={false}
            listingId={id}
            listingName={name}
            listingType={type}
            listingFirstPublished={firstPublished}
            orgName={orgName}
            orgType={org?.orgType ?? null}
            orgUrl={orgUrl}
            remoteOptions={remoteOptions}
            isIdealistDay={isIdealistDay}
            isExpired={false}
            actionButtons={
              <ViewEventActionButtons
                event={event}
                eventSaved={saved}
                onSaveEvent={save}
                onUnsaveEvent={unsave}
                orgSaved={orgSaved}
                onSaveOrg={saveOrg}
                onUnsaveOrg={unsaveOrg}
                shouldShowSaveOrgTooltip={shouldShowSaveOrgTooltip}
                onDismissSaveOrgTooltip={dismissSaveOrgTooltip}
              />
            }
          />

          <ListingViewSection title={i18n('Details')} noBorderTop>
            <EventDetailsList
              areasOfFocus={areasOfFocus}
              startDate={startDate}
              endDate={endDate}
              startsLocal={startsLocal}
              endsLocal={endsLocal}
              feeAmount={feeAmount}
              feeRequired={feeRequired}
              timezone={timezone}
              welcomeGroups={welcomeGroups}
              welcomeFamilies={welcomeFamilies}
              detailsWheelchairAccessible={detailsWheelchairAccessible}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={name}
            image={image}
            description={description}
            descriptionTruncated={descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && isExternalURL(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: [],
                  orgId: org?.id,
                  listingName: name,
                  listingType: 'EVENT',
                });
              }
            }}
            showMoreLinkTitle={i18n('Read more about this event')}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: event.type,
                locale: event.locale,
              });
            }}
          />

          <ListingViewLocationSection
            listingType={type}
            address={address}
            directions={directions}
            remoteOptions={remoteOptions}
          />

          <ListingViewApplySection
            applyEmail={applyEmail}
            applyText={applyText}
            applyTextTruncated={applyTextTruncated}
            applyUrl={applyUrl}
            applied={false}
            onApplied={null}
            onMarkAsApplied={() => Promise.resolve()}
            functions={[]}
            hasAts={false}
            listingName={name}
            listingType="EVENT"
            listingId={id}
            orgType={org?.orgType}
            orgId={org?.id}
            requestedAttachments={[]}
            requestedAttachmentsHash=""
            synonym={null}
            isPreview={false}
          />

          {event.hasComments && (
            <div data-qa-id="openweb-container-outer">
              {showComments && <ListingViewCommentSection listing={event} />}
            </div>
          )}

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>
      </ListingViewBody>

      {!user && (
        <PrintHidden>
          <ListingViewFooter>
            <ListingViewSignupCta
              listingUrl={url[CURRENT_LOCALE()]}
              type={type}
            />
          </ListingViewFooter>
        </PrintHidden>
      )}

      {showRsvpsModal && rsvpDataResult && (
        <EventShowAllRsvpsModalLoadable
          eventId={id}
          count={rsvpDataResult.count}
          initialRsvps={rsvpDataResult.rsvps}
          onClose={() => setShowRsvpsModal(false)}
        />
      )}
    </RsvpAnswerContext.Provider>
  );
}
